import { useState, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';

// Location with an iOS-shaped retry ladder:
//   1. quick low-accuracy request (fast on most devices)
//   2. on non-denial failure, retry with high accuracy (fixes many
//      iOS "position unavailable" cases)
//   3. last resort: a short watchPosition window — a known workaround
//      for intermittent CoreLocation failures in iOS Safari
// Denials (code 1) are terminal: retrying can never help, only settings.
export const useGeolocation = () => {
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Raw code+message from the browser, surfaced in the UI so failures
  // can be diagnosed remotely ("what does the gray line say?")
  const [errorDetail, setErrorDetail] = useState(null);
  // 'granted' | 'prompt' | 'denied' | null
  const [permissionState, setPermissionState] = useState(null);
  const busyRef = useRef(false);

  const refreshPermissionState = () => {
    if (navigator.permissions?.query) {
      navigator.permissions
        .query({ name: 'geolocation' })
        .then((status) => setPermissionState(status.state))
        .catch(() => {});
    }
  };

  const getLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by this browser');
      toast.error('Geolocation is not supported by this browser');
      return;
    }
    if (busyRef.current) return;
    busyRef.current = true;

    setLoading(true);
    setError(null);
    setErrorDetail(null);

    const succeed = (position) => {
      busyRef.current = false;
      const { latitude, longitude } = position.coords;
      setLocation({ latitude, longitude });
      setError(null);
      setErrorDetail(null);
      setLoading(false);
    };

    const fail = (geoError) => {
      busyRef.current = false;
      let errorMessage;
      switch (geoError.code) {
        case 1:
          errorMessage = 'Location access was denied';
          break;
        case 2:
          errorMessage = 'Your location could not be determined';
          break;
        case 3:
          errorMessage = 'Finding your location took too long';
          break;
        default:
          errorMessage = 'Something went wrong getting your location';
          break;
      }

      setError(errorMessage);
      setErrorDetail(`code ${geoError.code}${geoError.message ? `: ${geoError.message}` : ''}`);
      setLocation(null);
      setLoading(false);
      refreshPermissionState();
      toast.error(errorMessage);
    };

    // Step 1: quick low-accuracy
    navigator.geolocation.getCurrentPosition(
      succeed,
      (err1) => {
        if (err1.code === 1) {
          fail(err1); // denied — no retry will ever succeed
          return;
        }
        // Step 2: high accuracy, fresh fix, longer timeout
        navigator.geolocation.getCurrentPosition(
          succeed,
          (err2) => {
            // Step 3: brief watchPosition window
            let settled = false;
            const finish = (fn, arg) => {
              if (settled) return;
              settled = true;
              navigator.geolocation.clearWatch(watchId);
              clearTimeout(timerId);
              fn(arg);
            };
            const watchId = navigator.geolocation.watchPosition(
              (pos) => finish(succeed, pos),
              (err3) => {
                if (err3.code === 1) finish(fail, err3);
                // other watch errors: keep waiting until the timer expires
              },
              { enableHighAccuracy: true, maximumAge: 0 }
            );
            const timerId = setTimeout(() => finish(fail, err2), 12000);
          },
          { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
        );
      },
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 }
    );
  }, []);

  return {
    location,
    loading,
    error,
    errorDetail,
    permissionState,
    getLocation
  };
};
